import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from '@/lib/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useDocumentsStore } from '@/views/Documents/store'
import type {
  CopilotContextItem,
  CopilotProjectContext,
  CopilotTemplate,
  GeneratedDocument,
  DocumentType,
  DocumentEntityType,
  Project,
  Organization,
  Requirement,
  Meeting,
  Application,
  Attachment,
  Document,
} from '@/types'
import type { CopilotState } from './state'
import {
  buildRequirementContent,
  buildMeetingContent,
  buildApplicationContent,
} from '@/utils/contentBuilders'

interface ExtractContentResponse {
  success: boolean
  content: string
  extractedAt: Date
  cached: boolean
}

export const createActions = (state: CopilotState) => {
  const { logCreate } = useActivityLog()

  const selectProject = async (projectId: string): Promise<void> => {
    state.isLoadingContext = true
    state.error = null
    state.selectedProjectId = projectId
    state.contextItems = []
    state.selectedItemIds = []

    try {
      const tenantConstraint = getTenantConstraint()
      const tc = tenantConstraint ? [tenantConstraint] : []

      // Fetch project
      const projectDoc = await getDoc(doc(db, 'projects', projectId))
      if (!projectDoc.exists()) {
        throw new Error('Proyecto no encontrado')
      }
      const project = { id: projectDoc.id, ...projectDoc.data() } as Project

      // Fetch organization
      let orgName = ''
      if (project.organizationId) {
        const orgDoc = await getDoc(doc(db, 'organizations', project.organizationId))
        if (orgDoc.exists()) {
          const org = orgDoc.data() as Organization
          orgName = org.name
        }
      }

      // Fetch context document content
      let contextDocContent: string | undefined
      if (project.contextDocumentId) {
        const ctxDoc = await getDoc(doc(db, 'documents', project.contextDocumentId))
        if (ctxDoc.exists()) {
          contextDocContent = (ctxDoc.data() as Document).content
        }
      }

      const items: CopilotContextItem[] = []

      // Fetch documents for project
      const docsQuery = query(
        collection(db, 'documents'),
        ...tc,
        where('entityType', '==', 'project'),
        where('entityId', '==', projectId),
        orderBy('createdAt', 'desc')
      )
      const docsSnap = await getDocs(docsQuery)
      docsSnap.forEach((d) => {
        const data = d.data() as Document
        items.push({
          type: 'document',
          id: d.id,
          title: data.title,
          summary: `${data.type} - v${data.version || 1}`,
          content: data.content,
          entityType: data.type,
          selected: false,
        })
      })

      // Fetch attachments for project
      const attachQuery = query(
        collection(db, 'attachments'),
        ...tc,
        where('entityType', '==', 'project'),
        where('entityId', '==', projectId)
      )
      const attachSnap = await getDocs(attachQuery)
      attachSnap.forEach((d) => {
        const data = d.data() as Attachment
        items.push({
          type: 'attachment',
          id: d.id,
          title: data.originalName || data.fileName,
          summary: `${data.fileType} - ${formatFileSize(data.fileSize)}`,
          content: undefined, // Content must be extracted
          entityType: data.fileType,
          selected: false,
        })
      })

      // Fetch applications for project
      const appsQuery = query(
        collection(db, 'applications'),
        ...tc,
        where('projectId', '==', projectId)
      )
      const appsSnap = await getDocs(appsQuery)
      const appIds: string[] = []
      appsSnap.forEach((d) => {
        const data = d.data() as Application
        appIds.push(d.id)
        items.push({
          type: 'application',
          id: d.id,
          title: data.name,
          summary: `${data.platform} - ${data.status}`,
          content: buildApplicationContent(data),
          entityType: data.platform,
          selected: false,
        })
      })

      // Fetch documents and attachments for each application
      for (const appId of appIds) {
        const appDocsQuery = query(
          collection(db, 'documents'),
          ...tc,
          where('entityType', '==', 'application'),
          where('entityId', '==', appId),
          orderBy('createdAt', 'desc')
        )
        const appDocsSnap = await getDocs(appDocsQuery)
        appDocsSnap.forEach((d) => {
          const data = d.data() as Document
          items.push({
            type: 'document',
            id: d.id,
            title: `[App] ${data.title}`,
            summary: `${data.type} - v${data.version || 1}`,
            content: data.content,
            entityType: data.type,
            selected: false,
          })
        })

        const appAttachQuery = query(
          collection(db, 'attachments'),
          ...tc,
          where('entityType', '==', 'application'),
          where('entityId', '==', appId)
        )
        const appAttachSnap = await getDocs(appAttachQuery)
        appAttachSnap.forEach((d) => {
          const data = d.data() as Attachment
          items.push({
            type: 'attachment',
            id: d.id,
            title: `[App] ${data.originalName || data.fileName}`,
            summary: `${data.fileType} - ${formatFileSize(data.fileSize)}`,
            content: undefined,
            entityType: data.fileType,
            selected: false,
          })
        })
      }

      // Fetch requirements for project
      const reqsQuery = query(
        collection(db, 'requirements'),
        ...tc,
        where('projectId', '==', projectId),
        orderBy('createdAt', 'desc')
      )
      const reqsSnap = await getDocs(reqsQuery)
      reqsSnap.forEach((d) => {
        const data = d.data() as Requirement
        items.push({
          type: 'requirement',
          id: d.id,
          title: `${data.key} - ${data.title}`,
          summary: `${data.type} | ${data.definitionStatus} | ${data.developmentStatus}`,
          content: buildRequirementContent(data),
          entityType: data.type,
          selected: false,
        })
      })

      // Fetch meetings for project
      const meetingsQuery = query(
        collection(db, 'meetings'),
        ...tc,
        where('projectId', '==', projectId),
        orderBy('createdAt', 'desc')
      )
      const meetingsSnap = await getDocs(meetingsQuery)
      meetingsSnap.forEach((d) => {
        const data = d.data() as Meeting
        items.push({
          type: 'meeting',
          id: d.id,
          title: data.title,
          summary: `${data.meetingType} - ${data.status}`,
          content: buildMeetingContent(data),
          entityType: data.meetingType,
          selected: false,
        })
      })

      state.projectContext = {
        projectId,
        projectName: project.name,
        projectDescription: project.description || '',
        projectStatus: project.status,
        organizationId: project.organizationId,
        organizationName: orgName,
        contextDocumentContent: contextDocContent,
        items,
      }
      state.contextItems = items
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar el contexto del proyecto'
      console.error('Error loading project context:', error)
    } finally {
      state.isLoadingContext = false
    }
  }

  const toggleItemSelection = (itemId: string): void => {
    const idx = state.selectedItemIds.indexOf(itemId)
    if (idx >= 0) {
      state.selectedItemIds.splice(idx, 1)
    } else {
      state.selectedItemIds.push(itemId)
    }
  }

  const selectAllItems = (): void => {
    state.selectedItemIds = state.contextItems.map((item) => item.id)
  }

  const deselectAllItems = (): void => {
    state.selectedItemIds = []
  }

  const selectItemsByType = (type: string): void => {
    const typeIds = state.contextItems
      .filter((item) => item.type === type)
      .map((item) => item.id)
    // Add without duplicates
    typeIds.forEach((id) => {
      if (!state.selectedItemIds.includes(id)) {
        state.selectedItemIds.push(id)
      }
    })
  }

  const extractAttachmentContent = async (
    attachmentId: string,
    url: string,
    mimeType: string
  ): Promise<string> => {
    state.isExtractingContent = true

    try {
      const extractFn = httpsCallable<
        { attachmentId: string; url: string; mimeType: string },
        ExtractContentResponse
      >(functions, 'extractAttachmentContent')

      const result = await extractFn({ attachmentId, url, mimeType })

      if (result.data.success) {
        // Update the item's content
        const item = state.contextItems.find((i) => i.id === attachmentId)
        if (item) {
          item.content = result.data.content
        }
        return result.data.content
      }
      throw new Error('Error al extraer contenido')
    } catch (error) {
      console.error('Error extracting attachment content:', error)
      throw error
    } finally {
      state.isExtractingContent = false
    }
  }

  const buildContextPayload = () => {
    if (!state.projectContext) return null

    const selectedItems = state.contextItems
      .filter((item) => state.selectedItemIds.includes(item.id))
      .filter((item) => item.content)
      .map((item) => ({
        type: item.type,
        title: item.title,
        content: item.content || '',
      }))

    // Build requirements summary with counts by status
    const allReqs = state.contextItems.filter((i) => i.type === 'requirement')
    let requirementsSummary = ''
    if (allReqs.length > 0) {
      const statusCounts: Record<string, number> = {}
      const typeCounts: Record<string, number> = {}
      allReqs.forEach((r) => {
        const parts = r.summary?.split(' | ') || []
        if (parts[1]) statusCounts[parts[1]] = (statusCounts[parts[1]] || 0) + 1
        if (r.entityType) typeCounts[r.entityType] = (typeCounts[r.entityType] || 0) + 1
      })
      requirementsSummary = `Total: ${allReqs.length} requerimientos.`
      const statusStr = Object.entries(statusCounts).map(([s, c]) => `${s}: ${c}`).join(', ')
      if (statusStr) requirementsSummary += ` Por estado de definicion: ${statusStr}.`
      const typeStr = Object.entries(typeCounts).map(([t, c]) => `${t}: ${c}`).join(', ')
      if (typeStr) requirementsSummary += ` Por tipo: ${typeStr}.`
    }

    // Build meetings summary with counts by type and status
    const allMeetings = state.contextItems.filter((i) => i.type === 'meeting')
    let meetingsSummary = ''
    if (allMeetings.length > 0) {
      const typeCounts: Record<string, number> = {}
      allMeetings.forEach((m) => {
        if (m.entityType) typeCounts[m.entityType] = (typeCounts[m.entityType] || 0) + 1
      })
      meetingsSummary = `Total: ${allMeetings.length} reuniones.`
      const typeStr = Object.entries(typeCounts).map(([t, c]) => `${t}: ${c}`).join(', ')
      if (typeStr) meetingsSummary += ` Por tipo: ${typeStr}.`
    }

    return {
      projectId: state.projectContext.projectId,
      projectName: state.projectContext.projectName,
      projectDescription: state.projectContext.projectDescription,
      projectStatus: state.projectContext.projectStatus,
      organizationName: state.projectContext.organizationName,
      contextDocumentContent: state.projectContext.contextDocumentContent,
      selectedItems,
      templatePrompt: state.activeTemplate?.promptTemplate,
      requirementsSummary,
      meetingsSummary,
    }
  }

  const saveGeneratedDocument = async (
    document: GeneratedDocument,
    entityType: DocumentEntityType,
    entityId: string
  ): Promise<string> => {
    const documentsStore = useDocumentsStore()

    const docId = await documentsStore.createDocument({
      entityType,
      entityId,
      title: document.title,
      type: document.documentType as DocumentType,
      content: document.content,
      isPublic: true,
    })

    await logCreate('document', docId, document.title)

    return docId
  }

  const setTemplate = (template: CopilotTemplate | null): void => {
    state.activeTemplate = template
  }

  const clearContext = (): void => {
    state.selectedProjectId = null
    state.projectContext = null
    state.contextItems = []
    state.selectedItemIds = []
    state.activeTemplate = null
    state.generatedDocument = null
    state.currentSessionId = null
    state.error = null
  }

  return {
    selectProject,
    toggleItemSelection,
    selectAllItems,
    deselectAllItems,
    selectItemsByType,
    extractAttachmentContent,
    buildContextPayload,
    saveGeneratedDocument,
    setTemplate,
    clearContext,
  }
}

// Helper functions

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}
